'use client';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import {
  useCollectableCouponsQuery,
  useCollectCouponMutation,
} from '@/hooks/useCollectableCoupon';
import type { ICollectableCoupon } from '@/interfaces/collectableCoupon';
import { computeAdvancePage } from '../lib/advance';

export interface CollectableCouponListApi {
  displayedCoupons: ICollectableCoupon[];
  availableCoupons: ICollectableCoupon[];
  collectedCouponIds: string[];
  currentCoupon: ICollectableCoupon | null;
  setCurrentCoupon: (coupon: ICollectableCoupon | null) => void;
  handleCollect: (couponId?: string) => Promise<void>;
  /**
   * Advance the swiper to the page that should be visible after a
   * coupon has been collected. Returns the new page index (caller
   * may invoke `swiperRef.current?.slideToLoop(newPage)`).
   */
  computeNextPage: (collectedId: string, safePage: number) => number | null;
  isAuthenticated: boolean;
}

/**
 * Owns the coupon list state and the collect-mutation flow. Independent
 * of viewport / layout — `computeNextPage` accepts the current safePage
 * and grid-derived params so the caller drives the swiper.
 */
export const useCollectableCouponList = (params: {
  pageSize: number;
  viewportHeight: number;
}): CollectableCouponListApi => {
  const { isAuthenticated } = useReactQuerySession();
  const router = useRouter();
  const pathName = usePathname();
  const collectCouponMutation = useCollectCouponMutation();
  const { data: coupons } = useCollectableCouponsQuery();

  const [currentCoupon, setCurrentCoupon] = useState<ICollectableCoupon | null>(null);
  const [displayedCoupons, setDisplayedCoupons] = useState<ICollectableCoupon[]>([]);
  const [collectedCouponIds, setCollectedCouponIds] = useState<string[]>([]);

  // Sync from the query.
  useEffect(() => {
    if (coupons && coupons?.length > 0) {
      setDisplayedCoupons(coupons);
    }
  }, [coupons]);

  // Reset popup selection on route change.
  useEffect(() => {
    setCurrentCoupon(null);
  }, [pathName]);

  const handleCollect = async (couponId?: string) => {
    const id = couponId ?? currentCoupon?.Id;
    if (!id) return;

    if (!isAuthenticated) {
      setCurrentCoupon(null);
      // Caller is responsible for closing the popup and modal surfaces.
      router.push('/sign-in');
      return;
    }

    try {
      await collectCouponMutation.mutateAsync(id);
      setCollectedCouponIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setDisplayedCoupons((prev) => prev.filter((coupon) => coupon.Id !== id));
      setCurrentCoupon(null);
    } catch (error) {
      console.error('Error collecting coupon:', error);
    }
  };

  /**
   * Compute the page the swiper should land on after `collectedId` has
   * been removed. Returns `null` when no coupons remain (caller should
   * close the modal) or when the current page already has coupons (the
   * swiper will visually shift on re-render without needing slideTo).
   */
  const computeNextPage = (collectedId: string, safePage: number): number | null => {
    const remaining = displayedCoupons.filter(
      (c) => c.Id !== collectedId && !collectedCouponIds.includes(c.Id),
    );
    if (remaining.length === 0) return null;

    const { newPage } = computeAdvancePage(
      remaining,
      safePage,
      params.pageSize,
      params.viewportHeight,
    );

    if (newPage === safePage) return null;
    return newPage;
  };

  const availableCoupons = displayedCoupons.filter(
    (coupon) => !collectedCouponIds.includes(coupon.Id),
  );

  return {
    displayedCoupons,
    availableCoupons,
    collectedCouponIds,
    currentCoupon,
    setCurrentCoupon,
    handleCollect,
    computeNextPage,
    isAuthenticated,
  };
};